import { formatDateOnlyDMY, getUserFullName } from "helpers";
import {
  GroupedPreparationRow,
  MovementArticle,
  Order,
  Store,
  User,
} from "types";

const normalizeOptionalValue = (value?: string | null) => {
  const normalized = value?.trim() ?? "";
  return normalized.length > 0 ? normalized : null;
};

const getArticleAmountPerContainer = (article: MovementArticle) =>
  Number(
    article.article_locations?.amount_per_container ??
      article.amount_per_container ??
      0,
  );

const getOrderPreparationArticleLocationCode = (article: MovementArticle) =>
  article.article_locations?.location?.code ?? "";

const compareOrderPreparationLocationCode = (
  left: string,
  right: string,
) => left.localeCompare(right);

export const sortOrderPreparationArticlesByLocation = (
  articles: MovementArticle[] = [],
) =>
  [...articles].sort((left, right) => {
    const locationCompare = compareOrderPreparationLocationCode(
      getOrderPreparationArticleLocationCode(left),
      getOrderPreparationArticleLocationCode(right),
    );

    if (locationCompare !== 0) {
      return locationCompare;
    }

    const codeCompare = (
      left.code ??
      left.article_locations?.article?.code ??
      ""
    ).localeCompare(right.code ?? right.article_locations?.article?.code ?? "");

    if (codeCompare !== 0) {
      return codeCompare;
    }

    return String(left.id).localeCompare(String(right.id));
  });

export const getOrderPreparationArticleRemainingAmount = (
  article: MovementArticle,
) => {
  return Math.max(
    0,
    Number(article.article_locations?.stock ?? 0) -
      Number(article.article_locations?.reserved ?? 0),
  );
};

const getOrderPreparationArticleLocationId = (article: MovementArticle) =>
  Number(article.article_locations_id ?? article.article_locations?.id ?? 0);

const shouldCountRemainingAmountForArticleLocation = (
  articleLocationId: number,
  countedArticleLocationIds: Set<number>,
) => articleLocationId <= 0 || !countedArticleLocationIds.has(articleLocationId);

const markArticleLocationRemainingAmountAsCounted = (
  articleLocationId: number,
  countedArticleLocationIds: Set<number>,
) => {
  if (articleLocationId > 0) {
    countedArticleLocationIds.add(articleLocationId);
  }
};

const buildClientLabel = (client?: Order["client"] | null) => {
  if (!client) {
    return null;
  }

  const code = normalizeOptionalValue(client.code);
  const razonSocial = normalizeOptionalValue(client.razon_social);

  if (code && razonSocial) {
    return `${code} - ${razonSocial}`;
  }

  return code ?? razonSocial;
};

export const getOrderPreparationAssociatedClientLabel = (order: Order) =>
  normalizeOptionalValue(order.movement_order?.associated_client?.razon_social) ??
  normalizeOptionalValue(order.associated_client);

export const buildOrderArticleObservationWithAssociatedClient = (
  observation: string,
  associatedClientLabel: string | null,
) => {
  const normalizedObservation = normalizeOptionalValue(observation);
  const normalizedAssociatedClientLabel =
    normalizeOptionalValue(associatedClientLabel);
  const shouldAppendAssociatedClient =
    normalizedAssociatedClientLabel &&
    !normalizedObservation
      ?.toLocaleLowerCase()
      .includes(normalizedAssociatedClientLabel.toLocaleLowerCase());

  return [
    normalizedObservation,
    shouldAppendAssociatedClient
      ? `cliente asociado: ${normalizedAssociatedClientLabel}`
      : null,
  ]
    .map((value) => normalizeOptionalValue(value))
    .filter(Boolean)
    .join(" - ");
};

export const buildPreparationObservationLabel = (order: Order) => {
  const parts = [`Pedido ${order.id}`];
  const associatedClientLabel = getOrderPreparationAssociatedClientLabel(order);

  if (associatedClientLabel) {
    parts.push(`cliente asociado: ${associatedClientLabel}`);
  }

  return parts.join(", ");
};

export const getOrderPreparationTotalBoxes = (order: Order) =>
  (order.articles ?? []).reduce(
    (total, article) => total + Number(article.amount ?? 0),
    0,
  );

export const getOrderPreparationTotalUnits = (order: Order) =>
  (order.articles ?? []).reduce(
    (total, article) => total + Number(article.subtotal ?? 0),
    0,
  );

const getOrderPreparationStoreDescriptions = (order: Order, stores: Store[]) => {
  const labels = Array.from(
    new Set(
      (order.articles ?? [])
        .map((article) => {
          const articleStore = article.article_locations?.location?.store;

          if (articleStore?.description) {
            return articleStore.description.trim();
          }

          const fallbackStore = stores.find(
            (store) => store.id === article.article_locations?.location?.store_id,
          );

          return fallbackStore?.description?.trim() ?? "";
        })
        .filter((label) => label.length > 0),
    ),
  );

  return labels;
};

export const getOrderPreparationStoreLabel = (order: Order, stores: Store[]) => {
  const labels = getOrderPreparationStoreDescriptions(order, stores);
  return labels.length > 0 ? labels.join(" | ") : "-";
};

export const getOrderPreparationCreatorLabel = (order: Order, users: User[]) =>
  getUserFullName(order.created_by, users) || "Sin usuario asignado";

export const getOrderPreparationObservation = (order: Order) =>
  normalizeOptionalValue(order.observation) ?? "-";

export const getOrdersPreparationTotalBoxes = (orders: Order[]) =>
  orders.reduce((total, order) => total + getOrderPreparationTotalBoxes(order), 0);

export const getOrdersPreparationTotalUnits = (orders: Order[]) =>
  orders.reduce((total, order) => total + getOrderPreparationTotalUnits(order), 0);

export const getOrdersPreparationCreatorLabels = (
  orders: Order[],
  users: User[],
) => {
  const labels = Array.from(
    new Set(
      orders
        .map((order) => getOrderPreparationCreatorLabel(order, users))
        .filter((label) => label.length > 0),
    ),
  );

  return labels.length > 0 ? labels.join(" | ") : "-";
};

export const getOrdersPreparationClientLabels = (orders: Order[]) => {
  const labels = Array.from(
    new Set(
      orders
        .map((order) => buildClientLabel(order.client))
        .filter((label): label is string => Boolean(label)),
    ),
  );

  return labels.length > 0 ? labels.join(" | ") : "-";
};

export const getOrdersPreparationStoreLabels = (
  orders: Order[],
  stores: Store[],
) => {
  const labels = Array.from(
    new Set(
      orders.flatMap((order) => getOrderPreparationStoreDescriptions(order, stores)),
    ),
  );

  return labels.length > 0 ? labels.join(" | ") : "-";
};

export const buildGroupedPreparationRows = (
  orders: Order[],
): GroupedPreparationRow[] => {
  const grouped = new Map<string, GroupedPreparationRow>();
  const remainingArticleLocationIdsByGroup = new Map<string, Set<number>>();

  orders.forEach((order) => {
    const observationLabel = buildPreparationObservationLabel(order);

    (order.articles ?? []).forEach((article) => {
      const articleCode =
        article.code ??
        article.article_locations?.article?.code ??
        "sin codigo";
      const description =
        article.description ??
        article.article_locations?.article?.description ??
        "Sin descripcion";
      const locationCode =
        article.article_locations?.location?.code ?? "Sin ubicacion";
      const lote = normalizeOptionalValue(
        article.lote ?? article.article_locations?.lote ?? null,
      );
      const oc = normalizeOptionalValue(
        article.oc ?? article.article_locations?.oc ?? null,
      );
      const fechaIngreso = article.fecha_ingreso ?? article.article_locations?.fecha_ingreso ?? null;
      const fechaVencimiento =
        article.fecha_vencimiento ?? article.article_locations?.fecha_vencimiento ?? null;
      const amountPerContainer = getArticleAmountPerContainer(article);

      const key = [
        article.article_id ?? article.article_locations?.article?.id ?? "",
        locationCode,
        amountPerContainer,
        lote ?? "",
        oc ?? "",
        fechaIngreso ?? "",
        fechaVencimiento ?? "",
      ].join("|");

      const existing = grouped.get(key);
      const articleLocationId = getOrderPreparationArticleLocationId(article);
      const remainingArticleLocationIds =
        remainingArticleLocationIdsByGroup.get(key) ?? new Set<number>();
      const shouldAddRemainingAmount =
        shouldCountRemainingAmountForArticleLocation(
          articleLocationId,
          remainingArticleLocationIds,
        );

      if (existing) {
        existing.totalAmount += Number(article.amount ?? 0);
        if (shouldAddRemainingAmount) {
          existing.totalRemainingAmount += getOrderPreparationArticleRemainingAmount(
            article,
          );
          markArticleLocationRemainingAmountAsCounted(
            articleLocationId,
            remainingArticleLocationIds,
          );
          remainingArticleLocationIdsByGroup.set(key, remainingArticleLocationIds);
        }
        existing.totalUnits += Number(article.subtotal ?? 0);
        existing.totalWeight += Number(article.subtotal_kg ?? 0);

        if (!existing.observations.includes(observationLabel)) {
          existing.observations.push(observationLabel);
        }

        return;
      }

      grouped.set(key, {
        key,
        articleId: article.article_id ?? article.article_locations?.article?.id ?? null,
        articleCode,
        description,
        locationCode,
        lote,
        oc,
        fechaIngreso,
        fechaVencimiento,
        amountPerContainer,
        totalAmount: Number(article.amount ?? 0),
        totalRemainingAmount: getOrderPreparationArticleRemainingAmount(article),
        totalUnits: Number(article.subtotal ?? 0),
        totalWeight: Number(article.subtotal_kg ?? 0),
        observations: [observationLabel],
      });

      markArticleLocationRemainingAmountAsCounted(
        articleLocationId,
        remainingArticleLocationIds,
      );
      remainingArticleLocationIdsByGroup.set(key, remainingArticleLocationIds);
    });
  });

  return Array.from(grouped.values()).sort((left, right) => {
    const locationCompare = compareOrderPreparationLocationCode(
      left.locationCode,
      right.locationCode,
    );

    if (locationCompare !== 0) {
      return locationCompare;
    }

    const codeCompare = left.articleCode.localeCompare(right.articleCode);

    if (codeCompare !== 0) {
      return codeCompare;
    }

    return (left.lote ?? "").localeCompare(right.lote ?? "");
  });
};

export const sortOrdersForPreparationDetail = (orders: Order[]) =>
  [...orders].sort((left, right) => Number(left.id) - Number(right.id));

export const formatPreparationDateLabel = (value?: string | null) =>
  formatDateOnlyDMY(value ?? null) || "-";

export const buildPreparationDateRangeLabel = (orders: Order[]) => {
  const dateLabels = Array.from(
    new Set(
      orders
        .map((order) =>
          formatPreparationDateLabel(order.movement_order?.received_at),
        )
        .filter((label) => label !== "-"),
    ),
  ).sort((left, right) => left.localeCompare(right));

  if (dateLabels.length === 0) {
    return "-";
  }

  if (dateLabels.length === 1) {
    return dateLabels[0];
  }

  return `${dateLabels[0]} - ${dateLabels[dateLabels.length - 1]}`;
};

export const buildPreparationVehiclesLabel = (orders: Order[]) => {
  const vehicleLabels = Array.from(
    new Set(
      orders
        .map((order) => normalizeOptionalValue(order.vehicle))
        .filter((vehicle): vehicle is string => Boolean(vehicle)),
    ),
  ).sort((left, right) => left.localeCompare(right));

  if (vehicleLabels.length === 0) {
    return "-";
  }

  return vehicleLabels.join(" | ");
};
